<template>
  <div class="flex flex-col items-center justify-center">
    <h3 class="text-sm font-semibold text-gray-300 mb-4">Pitch / Roll Angle</h3>
    
    <!-- Attitude Indicator SVG -->
    <div class="relative" style="width: 320px; height: 320px;" :class="{ 'opacity-40': !isConnected }">
      <svg width="320" height="320" viewBox="0 0 320 320" class="drop-shadow-lg transition-opacity duration-300">
        <defs>
          <!-- Gradient for 3D effect -->
          <radialGradient id="gaugeGradient">
            <stop offset="0%" stop-color="#ffffff" />
            <stop offset="100%" stop-color="#f3f4f6" />
          </radialGradient>
          
          <!-- Clip path for inner circle -->
          <clipPath id="innerClip">
            <circle cx="160" cy="160" r="100" />
          </clipPath>
        </defs>
        
        <!-- Outer ring background -->
        <circle cx="160" cy="160" r="140" fill="none" stroke="#e5e7eb" stroke-width="24"/>
        
        <!-- Roll indicator (outer ring with horizon) -->
        <g :transform="`rotate(${realtime.roll}, 160, 160)`">
          <!-- Green horizon arc (180° from -90° to +90°) -->
          <path
            :d="createArc(160, 160, 140, -90, 90)"
            fill="none"
            :stroke="getRollColor(Math.abs(realtime.roll))"
            stroke-width="24"
            stroke-linecap="round"
          />
        </g>
        
        <!-- Inner background circle -->
        <circle cx="160" cy="160" r="110" fill="url(#gaugeGradient)" stroke="#d1d5db" stroke-width="2"/>
        
        <!-- Pitch indicator (inner vertical bar with horizon) -->
        <g clip-path="url(#innerClip)">
          <!-- Calculate pitch offset: +90° = top, -90° = bottom, 0° = center -->
          <!-- Pitch offset in pixels: (pitch / 90) * 100 -->
          <g :transform="`translate(0, ${-pitchOffset})`">
            <!-- Sky (upper half) -->
            <rect
              x="60"
              y="60"
              width="200"
              height="100"
              fill="#87ceeb"
              opacity="0.3"
            />
            
            <!-- Ground (lower half with color based on angle) -->
            <rect
              x="60"
              y="160"
              width="200"
              height="100"
              :fill="getPitchColor(Math.abs(realtime.pitch))"
              opacity="0.8"
            />
            
            <!-- Horizon line -->
            <line
              x1="60"
              x2="260"
              y1="160"
              y2="160"
              stroke="#ffffff"
              stroke-width="3"
            />
          </g>
          
          <!-- Fixed center reference marks (aircraft symbol) -->
          <g>
            <!-- Center dot -->
            <circle cx="160" cy="160" r="4" fill="#374151" />
            
            <!-- Left wing -->
            <line x1="120" y1="160" x2="140" y2="160" stroke="#374151" stroke-width="3" stroke-linecap="round"/>
            
            <!-- Right wing -->
            <line x1="180" y1="160" x2="200" y2="160" stroke="#374151" stroke-width="3" stroke-linecap="round"/>
          </g>
        </g>
        
        <!-- Outer ring border -->
        <circle cx="160" cy="160" r="152" fill="none" stroke="#9ca3af" stroke-width="1"/>
        <circle cx="160" cy="160" r="128" fill="none" stroke="#9ca3af" stroke-width="1"/>
        
        <!-- Roll tick marks -->
        <g v-for="angle in [-90, -60, -45, -30, 0, 30, 45, 60, 90]" :key="angle">
          <line
            :x1="160 + 128 * Math.cos((angle - 90) * Math.PI / 180)"
            :y1="160 + 128 * Math.sin((angle - 90) * Math.PI / 180)"
            :x2="160 + 140 * Math.cos((angle - 90) * Math.PI / 180)"
            :y2="160 + 140 * Math.sin((angle - 90) * Math.PI / 180)"
            stroke="#6b7280"
            :stroke-width="angle === 0 ? 3 : 2"
          />
        </g>
        
        <!-- Top triangle pointer for roll -->
        <path
          d="M 160 20 L 150 35 L 170 35 Z"
          fill="#374151"
        />
      </svg>
      
      <!-- Value displays -->
      <div class="absolute bottom-2 left-1/2 transform -translate-x-1/2 text-center bg-[#13183b]/95 px-4 py-2 rounded-lg shadow-lg border border-[#2d3a70]">
        <div class="flex gap-4 text-sm">
          <div>
            <div class="text-xs text-gray-400 font-semibold">PITCH</div>
            <div class="text-2xl font-bold" :class="getPitchTextClass(realtime.pitch)">
              {{ formatAngle(realtime.pitch) }}
            </div>
          </div>
          <div class="border-l border-[#2d3a70]"></div>
          <div>
            <div class="text-xs text-gray-400 font-semibold">ROLL</div>
            <div class="text-2xl font-bold" :class="getRollTextClass(realtime.roll)">
              {{ formatAngle(realtime.roll) }}
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <p class="text-xs text-gray-500 mt-3">Range: ±90°</p>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useDeviceStore } from '../stores/device.js';
import { useConfigStore } from '../stores/config.js';

const deviceStore = useDeviceStore();
const configStore = useConfigStore();

const isConnected = computed(() => deviceStore.isConnected);
const realtime = computed(() => deviceStore.realtime);

// Get warning and alarm angles from config
const pitchWarning = computed(() => configStore.parsedConfig?.pitchWarning || 30);
const rollWarning = computed(() => configStore.parsedConfig?.rollWarning || 30);
const alarmAngle = computed(() => configStore.parsedConfig?.alarmAngle || 45);

// Calculate pitch offset in pixels (±90° -> ±100px)
const pitchOffset = computed(() => {
  return (realtime.value.pitch / 90) * 100;
});

// Create SVG arc path
function createArc(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  return [
    "M", start.x, start.y, 
    "A", r, r, 0, largeArcFlag, 0, end.x, end.y
  ].join(" ");
}

function polarToCartesian(cx, cy, r, angle) {
  const angleInRadians = (angle - 90) * Math.PI / 180.0;
  return {
    x: cx + (r * Math.cos(angleInRadians)),
    y: cy + (r * Math.sin(angleInRadians))
  };
}

// Color functions for pitch
function getPitchColor(angle) {
  const abs = Math.abs(angle);
  if (abs >= alarmAngle.value) return '#ef4444'; // red (alarm)
  if (abs >= pitchWarning.value) return '#f59e0b'; // amber (warning)
  return '#22c55e'; // green (safe)
}

function getPitchTextClass(angle) {
  const abs = Math.abs(angle);
  if (abs >= alarmAngle.value) return 'text-danger-600';
  if (abs >= pitchWarning.value) return 'text-warning-600';
  return 'text-green-400'; // Brighter green
}

// Color functions for roll
function getRollColor(angle) {
  const abs = Math.abs(angle);
  if (abs >= alarmAngle.value) return '#ef4444'; // red (alarm)
  if (abs >= rollWarning.value) return '#f59e0b'; // amber (warning)
  return '#22c55e'; // green (safe)
}

function getRollTextClass(angle) {
  const abs = Math.abs(angle);
  if (abs >= alarmAngle.value) return 'text-danger-600';
  if (abs >= rollWarning.value) return 'text-warning-600';
  return 'text-green-400'; // Brighter green
}

// Format functions
function formatAngle(angle) {
  if (angle === 0 && !isConnected.value) return '--';
  return Math.round(angle) + '°';
}
</script>
